import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { updateBusinessInfo, uploadBusinessLogo } from "../../../../redux/reducers/businessSlice";
import Spinner from "../../../../components/Spinner";
import PhoneInput from "../../../../components/common/PhoneInput";
import { LuCamera, LuCloudUpload, LuSparkles } from 'react-icons/lu';
import AiLogoModal from "./AiLogoModal";

export default function Basic({ data: businessdata }) {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.business);
  const [showAiModal, setShowAiModal] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().min(3, "Too short").required("Business name is required"),
    contact: Yup.object({
      phone: Yup.object({
        countryCode: Yup.string().required("Required"),
        number: Yup.string().required("Phone is required"),
      }),
    }),
    // countryOfOperation: Yup.string().required("Country is required"),
    location: Yup.string().required("Location is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: businessdata?.name || "",
      contact: {
        email: businessdata?.contact?.email || "",
        phone: typeof businessdata?.contact?.phone === 'string' 
          ? { countryCode: '+1', number: businessdata?.contact?.phone } 
          : businessdata?.contact?.phone || { countryCode: '+1', number: "" },
      },
      // countryOfOperation: businessdata?.countryOfOperation || "",
      location: businessdata?.location || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Remove email from update to avoid issues if backend doesn't allow it
      const { contact, ...rest } = values;
      const filteredData = {
        ...rest,
        contact: {
          phone: contact.phone
        }
      };
      dispatch(updateBusinessInfo({ id: businessdata?._id, data: filteredData }));
    },
  });

  return (
    <Spinner active={loading}>
      <div className="display-card">
        <h3>Basic Information</h3>
        <p>Enter and Update your Basic Informations</p>

        {/* Logo Upload Section */}
        <div className="d-flex flex-column align-items-center mb-4 pb-4 border-bottom">
            <div className="position-relative">
                <div 
                    className="bg-light rounded-circle border d-flex align-items-center justify-content-center shadow-sm"
                    style={{ width: '120px', height: '120px', overflow: 'hidden', position: 'relative' }}
                >
                    {businessdata?.logo ? (
                        <img src={businessdata.logo} alt="Business Logo" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                    ) : (
                        <LuCamera size={40} className="text-muted" style={{ opacity: 0.5 }} />
                    )}
                    {loading && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex align-items-center justify-content-center rounded-circle">
                            <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                        </div>
                    )}
                </div>
                <label 
                    htmlFor="logo-upload" 
                    className="position-absolute bottom-0 end-0 text-white p-2 rounded-circle shadow-sm cursor-pointer hover-scale"
                    style={{ cursor: 'pointer', backgroundColor: '#28B0C4', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    <LuCloudUpload size={16} />
                    <input 
                        type="file" 
                        id="logo-upload" 
                        hidden 
                        accept="image/*"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                dispatch(uploadBusinessLogo({ id: businessdata?._id, file }));
                            }
                        }}
                    />
                </label>
            </div>
            <div className="mt-3 text-center">
                <h6 className="fw-bold mb-1">Business Logo</h6>
                <small className="text-muted d-block mb-3">High resolution JPG or PNG preferred</small>
                <button 
                    type="button"
                    className="btn btn-outline-primary rounded-pill px-3 shadow-xs d-inline-flex align-items-center gap-2"
                    style={{ borderColor: '#28B0C4', color: '#28B0C4' }}
                    onClick={() => setShowAiModal(true)}
                >
                    <LuSparkles size={14} style={{ color: '#28B0C4' }} />
                    Generate Logo with AI
                </button>
            </div>
            <AiLogoModal 
                show={showAiModal} 
                onHide={() => setShowAiModal(false)} 
                business={businessdata} 
            />
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="form-group row">
            <div className="col-xxl-4 col-lg-4 col-md-6 col-12 mb-3">
              <label>
                Business Name <span className="required">*</span>
              </label>
              <input
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                className={`gbl-input ${formik.touched.name && formik.errors.name ? "is-invalid" : ""}`}
                placeholder="Enter Business Name"
              />
              {formik.touched.name && formik.errors.name && (
                <div className="invalid-feedback">{formik.errors.name}</div>
              )}
            </div>
            <div className="col-xxl-4 col-lg-4 col-md-6 col-12 mb-3">
              <label>
                Business Email <span className="required">*</span>
              </label>
              <input
                value={formik.values.contact.email}
                className="gbl-input"
                placeholder="Enter Business Email"
                disabled
              />
            </div>
            <div className="col-xxl-4 col-lg-4 col-md-6 col-12 mb-3">
              <PhoneInput
                name="contact.phone"
                label="Business Contact"
                value={formik.values.contact.phone}
                onChange={(val) => formik.setFieldValue('contact.phone', val)}
                error={formik.errors.contact?.phone?.number}
                touched={formik.touched.contact?.phone?.number}
                required
              />
            </div>
            {/* <div className="col-xxl-4 col-lg-4 col-md-6 col-12 mb-3">
              <label>
                Country <span className="required">*</span>
              </label>
              <select
                name="countryOfOperation"
                value={formik.values.countryOfOperation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                className={`gbl-input ${formik.touched.countryOfOperation && formik.errors.countryOfOperation ? "is-invalid" : ""}`}
              >
                <option value="">Select Country</option>
                <option value="canada">Canada</option>
                <option value="usa">USA</option>
              </select>
              {formik.touched.countryOfOperation && formik.errors.countryOfOperation && (
                <div className="invalid-feedback">{formik.errors.countryOfOperation}</div>
              )}
            </div> */}
            <div className="col-xxl-8 col-lg-8 col-md-12 col-12 mb-3">
              <label>
                Location <span className="required">*</span>
              </label>
              <input
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading}
                className={`gbl-input ${formik.touched.location && formik.errors.location ? "is-invalid" : ""}`}
                placeholder="Enter Location"
              />
              {formik.touched.location && formik.errors.location && (
                <div className="invalid-feedback">{formik.errors.location}</div>
              )}
            </div>
          </div>
          <div className="form-group row justify-content-center mt-3">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading || !formik.dirty}
              style={{ width: '200px' }}
            >
              {loading ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </Spinner>
  );
}
